import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, of, shareReplay, tap, throwError } from 'rxjs';

const httpOptions = {
  headers: new HttpHeaders({
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }),
};

interface CacheItem {
  data: any;
  expiry: number;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  BaseUrl: any = 'https://api1.btechmart.in/api/';
  imageUrl: any = 'https://api1.btechmart.in/';

  // In-memory cache for GET/POST fetch endpoints
  private responseCache = new Map<string, CacheItem>();
  // In-flight request deduplication map
  private inFlightRequests = new Map<string, Observable<any>>();

  constructor(private http: HttpClient) { }

  /**
   * Deep clones data to prevent component mutations (e.g., prefixing imageUrl)
   * from corrupting cached data for subsequent subscribers.
   */
  private clone<T>(data: T): T {
    try {
      if (typeof structuredClone === 'function') {
        return structuredClone(data);
      }
    } catch { }
    return JSON.parse(JSON.stringify(data));
  }

  /**
   * Determine if an endpoint is safe to cache and deduplicate.
   */
  private isCacheableEndpoint(url: string): boolean {
    const cleanUrl = url.trim().toLowerCase();
    // Exclude mutations / transactions / auth / forms
    if (
      cleanUrl.includes('create') ||
      cleanUrl.includes('update') ||
      cleanUrl.includes('delete') ||
      cleanUrl.includes('payment') ||
      cleanUrl.includes('otp') ||
      cleanUrl.includes('validate') ||
      cleanUrl.includes('book')
    ) {
      return false;
    }

    return (
      cleanUrl.startsWith('fetch/') ||
      cleanUrl.startsWith('get/') ||
      cleanUrl.includes('/segment') ||
      cleanUrl.includes('/category') ||
      cleanUrl.includes('/subcategories') ||
      cleanUrl.includes('feedback/get')
    );
  }

  /**
   * Get TTL for cacheable endpoint (in ms)
   */
  private getTtlForUrl(url: string): number {
    const cleanUrl = url.trim().toLowerCase();
    // Segments, categories, subcategories, banners, reviews rarely change (cache 5 mins)
    if (
      cleanUrl.includes('segment') ||
      cleanUrl.includes('category') ||
      cleanUrl.includes('subcategories') ||
      cleanUrl.includes('product_services') ||
      cleanUrl.includes('feedback')
    ) {
      return 5 * 60 * 1000; // 5 minutes
    }
    // Dynamic lists (products, blogs, etc.) cache 2 minutes
    return 2 * 60 * 1000;
  }

  /**
   * Clear cache (useful when an item is modified or created)
   */
  clearCache(pattern?: string): void {
    if (!pattern) {
      this.responseCache.clear();
      return;
    }
    for (const key of this.responseCache.keys()) {
      if (key.includes(pattern)) {
        this.responseCache.delete(key);
      }
    }
  }

  commonGetfun(url: string): Observable<any> {
    if (!this.isCacheableEndpoint(url)) {
      return this.http.get(this.BaseUrl + `${url}`, httpOptions);
    }
    const cacheKey = `GET_${url}`;
    return this.handleCachedRequest(cacheKey, url, () =>
      this.http.get(this.BaseUrl + `${url}`, httpOptions)
    );
  }

  commonPostfun(url: string, body: any): Observable<any> {
    if (!this.isCacheableEndpoint(url)) {
      return this.http.post(this.BaseUrl + `${url}`, body, httpOptions);
    }
    const cacheKey = `POSTFUN_${url}_${JSON.stringify(body || {})}`;
    return this.handleCachedRequest(cacheKey, url, () =>
      this.http.post(this.BaseUrl + `${url}`, body, httpOptions)
    );
  }

  commonPost(url: string, body: any): Observable<any> {
    if (!this.isCacheableEndpoint(url)) {
      return this.http.post(this.BaseUrl + `${url}`, body);
    }
    const cacheKey = `POST_${url}_${JSON.stringify(body || {})}`;
    return this.handleCachedRequest(cacheKey, url, () =>
      this.http.post(this.BaseUrl + `${url}`, body)
    );
  }

  private handleCachedRequest(
    cacheKey: string,
    url: string,
    requestFactory: () => Observable<any>
  ): Observable<any> {
    const now = Date.now();

    // 1. Check in-memory cache
    const cached = this.responseCache.get(cacheKey);
    if (cached && cached.expiry > now) {
      return of(this.clone(cached.data));
    }

    // 2. Check if identical request is currently in-flight (deduplication)
    const inFlight$ = this.inFlightRequests.get(cacheKey);
    if (inFlight$) {
      return inFlight$.pipe(map((data) => this.clone(data)));
    }

    // 3. Initiate request, share across subscribers, and store in cache
    const ttl = this.getTtlForUrl(url);
    const request$ = requestFactory().pipe(
      tap((response) => {
        // Only cache valid responses
        if (response && (response.status !== false || response.statusCode === 200)) {
          this.responseCache.set(cacheKey, {
            data: this.clone(response),
            expiry: Date.now() + ttl,
          });
        }
      }),
      finalize(() => {
        this.inFlightRequests.delete(cacheKey);
      }),
      shareReplay(1)
    );

    this.inFlightRequests.set(cacheKey, request$);
    return request$.pipe(map((data) => this.clone(data)));
  }

  getAccurateLocation(): Promise<{ latitude: number; longitude: number }> {
    return new Promise((resolve, reject) => {
      if ('geolocation' in navigator) {
        const watchId = navigator.geolocation.watchPosition(
          (position) => {
            navigator.geolocation.clearWatch(watchId); // Get the first accurate update
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          (error) => {
            navigator.geolocation.clearWatch(watchId);
            reject(error);
          },
          {
            enableHighAccuracy: true,
            timeout: 500, // 1.5 seconds is very short
            maximumAge: 0,
          }
        );
      } else {
        reject('Geolocation is not supported by this browser.');
      }
    });
  }

  getAddressFromCoordinates(latitude: number, longitude: number) {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`;
    return this.http.get(url);
  }

  createOrder(amount: any) {
    return this.http.post<any>(`${this.BaseUrl}payment/create-order`, { amount });
  }

  verifyPayment(data: any) {
    return this.http.post<any>(`${this.BaseUrl}payment/verify-payment`, data);
  }
}
